import { useState, forwardRef } from 'react';
import cx from 'classnames';
import Error from '@mui/icons-material/Error';
import LockIcon from '@mui/icons-material/Lock';
import { TextField as MuiTextField, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Typography from '@classify/react-core-components/Typography';
import { alpha } from '@classify/react-core-components/styles';

export const useStyles = makeStyles(
  /* istanbul ignore next */ ({ breakpoints, spacing, palette }) => ({
    textField: {
      marginBottom: spacing(2),
    },
    textInputFullWidth: {
      width: '100%',
    },
    noAsterisk: {
      display: 'none',
    },
    root: {
      color: palette.text.secondary,
    },
    focused: {
      color: [palette.primary.main, '!important'],
    },
    shrink: {
      fontSize: 14,
      transform: `translate(${spacing(1)}, ${spacing(1)}) scale(0.75) !important`,
    },
    formControl: {
      fontSize: 14,
      padding: spacing(3, 1, 1, 1),
      height: 16,
      lineHeight: '16px',
    },
    formControlMultiline: {
      height: 'unset',
    },
    formControlNoLabel: {
      padding: spacing(2, 1, 2, 1),
      minHeight: `initial !important;`,
    },
    multiline: {
      paddingLeft: spacing(1),
      paddingRight: spacing(1),
    },
    multilineInput: {
      padding: 0,
      lineHeight: '24px',
      backgroundColor: 'transparent !important',
    },
    multilineNoLabel: {
      // Custom padding to align it with single line inputs
      paddingTop: spacing(1.5),
      paddingBottom: '11px',
    },
    disabled: {
      color: palette.text.secondary,
      'div&': {
        backgroundColor: [alpha(palette.status.inactive, 0.04), '!important'],
      },
    },
    filledLabel: {
      transform: `translate(${spacing(1)}, 17px) scale(1)`,
    },
    filled: {
      backgroundColor: [alpha(palette.primary.main, 0.04), '!important'],
      '&:hover': {
        backgroundColor: alpha(palette.primary.main, 0.04),
        '&:after': {
          transform: 'scale(1)',
        },
      },
      '& input': {
        backgroundColor: 'transparent !important',
      },
    },
    error: {
      backgroundColor: [alpha(palette.status.danger, 0.04), '!important'],
    },
    adornedEnd: {
      color: palette.text.secondary,
    },
    inputAdornedEnd: {
      color: 'initial',
    },
    helperContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    maxLength: {
      marginTop: 3,
      whiteSpace: 'nowrap',
      color: palette.text.secondary,
    },
    endAdornmentMultiline: {
      alignSelf: 'flex-start',
    },
    [breakpoints.down('sm')]: {
      filledLabel: {
        transform: `translate(${spacing(1)}, 13px) scale(1)`,
      },
    },
  }),
  { classNamePrefix: 'TextField' }
);

const TextField = forwardRef(
  (
    {
      formHelper = null,
      noAsterisk = false,
      field = {},
      onBlur = null,
      fieldSuggestion = null,
      inputProps = {},
      label = null,
      error = null,
      name,
      disabled = false,
      icon = null,
      fullWidth = true,
      InputProps = {},
      className = '',
      onChange = () => {},
      maxLength = null,
      multiline = false,
      select = false,
      ...otherProps
    },
    ref
  ) => {
    const classes = useStyles();
    const [currentLength, setCurrentLength] = useState(0);

    let endAdornment = null;
    if (icon) {
      endAdornment = icon;
    } else if (error) {
      endAdornment = (
        <Error
          color="error"
          fontSize="small"
          className={cx({ [classes.endAdornmentMultiline]: multiline })}
        />
      );
    } else if (disabled) {
      endAdornment = <LockIcon className={cx({ [classes.endAdornmentMultiline]: multiline })} />;
    }

    const helperErrorId = `${name}error`;
    const helperId = `${name}helper`;
    let ariaDescribedBy;

    if (error) {
      ariaDescribedBy = helperErrorId;
    } else if (formHelper) {
      ariaDescribedBy = helperId;
    }

    return (
      <div className={cx(classes.textField, className)}>
        <MuiTextField
          ref={ref}
          id={`field${name}`}
          name={name}
          className={fullWidth ? classes.textInputFullWidth : null}
          variant="filled"
          error={!!error}
          label={label}
          disabled={disabled}
          onChange={(e) => {
            if (maxLength) {
              setCurrentLength(e.target.value?.length);
            }
            onChange(e);
          }}
          multiline={multiline}
          select={select}
          {...otherProps}
          slotProps={{
            input: {
              endAdornment,
              onBlur: (evt) => {
                if (field.onBlur) field.onBlur(evt);
                if (onBlur) onBlur(evt);
              },
              classes: {
                input: cx(
                  classes.formControl,
                  { [classes.formControlNoLabel]: !label },
                  { [classes.formControlMultiline]: multiline }
                ),
                root: !disabled ? classes.filled : null,
                error: classes.error,
                multiline: cx(classes.multiline, { [classes.multilineNoLabel]: !label }),
                inputMultiline: classes.multilineInput,
                disabled: classes.disabled,
                adornedEnd: classes.adornedEnd,
                inputAdornedEnd: classes.inputAdornedEnd,
              },
              inputProps: {
                maxLength,
                'aria-describedby': ariaDescribedBy,
                ...inputProps,
              },
              ...InputProps,
            },

            inputLabel: {
              classes: {
                ...(noAsterisk ? { asterisk: classes.noAsterisk } : {}),
                root: classes.root,
                focused: classes.focused,
                shrink: classes.shrink,
                filled: classes.filledLabel,
              },
            },
          }}
        />
        {fieldSuggestion}
        <div className={classes.helperContainer}>
          <div>
            {error && (
              <FormHelperText id={helperErrorId} error>
                {error}
              </FormHelperText>
            )}
            {formHelper && <FormHelperText id={helperId}>{formHelper}</FormHelperText>}
          </div>
          {maxLength && (
            <Typography variant="caption" className={classes.maxLength}>
              {`${currentLength}/${maxLength}`}
            </Typography>
          )}
        </div>
      </div>
    );
  }
);

export default TextField;
